import {
  NOTIFICATIONS_QUERY_KEY,
  NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
} from "@frontend-shared/constants";
import type {
  IParsedNotification,
  IUnreadCountResponse,
} from "@frontend-shared/types";
import { applyNotificationCreatedToUnreadCount } from "@frontend-shared/utils";
import type { QueryClient } from "@tanstack/react-query";

import { schedulePushRefetch } from "@/lib/push-refetch-scheduler";

interface IApplyPushToUnreadCountProps {
  queryClient: QueryClient;
  notification: IParsedNotification;
}

const UNREAD_COUNT_QUERY_KEY = [NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY];

const patchAndScheduleRefetch = ({
  queryClient,
  notification,
}: IApplyPushToUnreadCountProps): void => {
  // NOTE: Patch first — a cache write clears the stale flag the scheduler
  // sets right after.
  queryClient.setQueryData<IUnreadCountResponse>(
    UNREAD_COUNT_QUERY_KEY,
    (oldData) =>
      applyNotificationCreatedToUnreadCount({ oldData, notification }),
  );
  schedulePushRefetch([[NOTIFICATIONS_QUERY_KEY], UNREAD_COUNT_QUERY_KEY]);
};

// NOTE: NSAI-5911 — a `notification:created` push moves the badge (bell +
// favicon) at once from the payload, and the list + count refetch is
// scheduled (coalesced, deferred while hidden). One race needs handling: a
// count request already in flight left BEFORE the pushed row existed, so its
// response would land on top of the patch and drop the badge back by one
// until the scheduled refetch. That request is cancelled first (React Query
// reverts the query to its pre-fetch data), then the patch is applied. With
// nothing cached yet there is no badge to patch, and an initial load is left
// alone rather than delayed.
export const applyPushToUnreadCount = ({
  queryClient,
  notification,
}: IApplyPushToUnreadCountProps): void => {
  const query = queryClient
    .getQueryCache()
    .find({ queryKey: UNREAD_COUNT_QUERY_KEY, exact: true });
  const isRefetchInFlight =
    query?.state.fetchStatus === "fetching" && query.state.data !== undefined;

  if (!isRefetchInFlight) {
    patchAndScheduleRefetch({ queryClient, notification });
    return;
  }

  void queryClient
    .cancelQueries({ queryKey: UNREAD_COUNT_QUERY_KEY, exact: true })
    .then(() => {
      patchAndScheduleRefetch({ queryClient, notification });
    });
};
